import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import api from "../api/client";
import { useCustomer } from "../context/CustomerContext";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { formatDateTime } from "../utils/datetime";

/**
 * Google Workspace users and groups.
 *
 * THE GAP THIS CLOSES. The Workspace adapter has had full user and group
 * management for a long time — list, create, suspend, unsuspend, reset a
 * password, add and remove group members — and there was no screen anywhere in
 * the console that reached any of it. You could create a Workspace connector,
 * watch it test green, and then have nothing to click. Compute, Storage and
 * Networking each got a page; the fourth capability group, "Users & groups",
 * did not, so Workspace was the one platform you could connect and not use.
 *
 * WHICH BUTTONS EXIST IS DECIDED BY THE BACKEND, the same rule as the other
 * cloud screens: capabilities come from /supported-platforms, which derives
 * them from the adapter. A capability the adapter does not implement produces
 * no button, rather than one that fails when pressed.
 *
 * PASSWORDS ARE NEVER GENERATED HERE. A password typed by an admin goes
 * through the server, which is where the password policy lives — the browser
 * is not the control. It is shown once, in the field the admin typed it into,
 * and never returned by any endpoint afterwards.
 */

type Cap = { key: string; label: string; writes: boolean; detail: string };
type Platform = {
  platform: string;
  label: string;
  groups: { key: string; label: string; capabilities: Cap[] }[];
};
type Connector = {
  id: number;
  platform: string;
  platform_label: string;
  name: string;
  enabled: boolean;
  customer_id: number;
};
type WsUser = {
  id: string;
  primary_email: string;
  name: string | null;
  given_name: string | null;
  family_name: string | null;
  is_admin: boolean | null;
  is_suspended: boolean | null;
  ou_path: string | null;
  last_login_at: string | null;
  created_at: string | null;
};
type WsGroup = {
  id: string;
  email: string;
  name: string | null;
  description: string | null;
  direct_members_count: number;
};
type WsMember = {
  id: string;
  email: string;
  role: string | null;
  type: string | null;
  status: string | null;
};

const ROLES = ["MEMBER", "MANAGER", "OWNER"];

export default function CloudIdentity() {
  const { currentCustomerId } = useCustomer();
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [selected, setSelected] = useState<number | "">("");
  const [tab, setTab] = useState<"users" | "groups">("users");

  const [users, setUsers] = useState<WsUser[]>([]);
  const [groups, setGroups] = useState<WsGroup[]>([]);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [members, setMembers] = useState<WsMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    primary_email: "",
    given_name: "",
    family_name: "",
    password: "",
  });
  const [resetting, setResetting] = useState<WsUser | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [addingTo, setAddingTo] = useState<WsGroup | null>(null);
  const [newMember, setNewMember] = useState({ member_email: "", role: "MEMBER" });

  const flash = (m: string) => {
    setMsg(m);
    setTimeout(() => setMsg(""), 4000);
  };

  useEffect(() => {
    Promise.all([
      api.get("/api/v1/cloud-connectors/supported-platforms"),
      api.get("/api/v1/cloud-connectors"),
    ])
      .then(([p, c]) => {
        setPlatforms(p.data || []);
        setConnectors(c.data || []);
      })
      .catch(() => setErr("Could not load cloud connectors."));
  }, []);

  // Only connectors on a platform that actually declares an identity group.
  const usable = useMemo(() => {
    const withIdentity = new Set(
      platforms.filter((p) => p.groups.some((g) => g.key === "identity")).map((p) => p.platform),
    );
    return connectors.filter(
      (c) =>
        c.enabled &&
        withIdentity.has(c.platform) &&
        (!currentCustomerId || c.customer_id === currentCustomerId),
    );
  }, [platforms, connectors, currentCustomerId]);

  useEffect(() => {
    if (!selected && usable.length === 1) setSelected(usable[0].id);
  }, [usable, selected]);

  const current = usable.find((c) => c.id === selected);

  const caps = useMemo(() => {
    const p = platforms.find((x) => x.platform === current?.platform);
    const g = p?.groups.find((x) => x.key === "identity");
    return new Set((g?.capabilities || []).map((c) => c.key));
  }, [platforms, current]);

  const canAdminUsers = caps.has("gws_user_admin");
  const canReadMembers = caps.has("gws_group_members");
  const canAdminGroups = caps.has("gws_group_admin");

  const load = useCallback(async () => {
    if (!current) return;
    setLoading(true);
    setErr("");
    const base = `/api/v1/cloud-connectors/${current.id}`;
    try {
      const [u, g] = await Promise.all([
        api.get(`${base}/gws-users`),
        api.get(`${base}/gws-groups`),
      ]);
      setUsers(u.data || []);
      setGroups(g.data || []);
    } catch (e: any) {
      // The overwhelmingly likely cause is the one manual step: domain-wide
      // delegation not yet authorised. Say so instead of "request failed".
      const detail = e?.response?.data?.detail || "";
      setErr(
        /unauthorized|forbidden|403|delegation|access_denied/i.test(detail)
          ? `${detail} — this usually means domain-wide delegation has not been ` +
              `authorised for this service account in the Workspace admin console.`
          : detail || "Could not read Google Workspace.",
      );
      setUsers([]);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [current]);

  useEffect(() => {
    if (selected) load();
  }, [selected, load]);

  const loadMembers = async (g: WsGroup) => {
    if (!current) return;
    if (openGroup === g.email) {
      setOpenGroup(null);
      return;
    }
    setOpenGroup(g.email);
    setMembers([]);
    setMembersLoading(true);
    try {
      const r = await api.get(
        `/api/v1/cloud-connectors/${current.id}/gws-groups/${encodeURIComponent(g.email)}/members`,
      );
      setMembers(r.data || []);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || `Could not read the members of ${g.email}.`);
    } finally {
      setMembersLoading(false);
    }
  };

  // ── writes. Each one changes a customer's tenant, so each confirms. ─────

  const setSuspended = async (u: WsUser, suspend: boolean) => {
    if (!current) return;
    const verb = suspend ? "Suspend" : "Restore";
    if (
      !window.confirm(
        `${verb} ${u.primary_email}?\n\n` +
          (suspend
            ? "They lose access to mail and every Workspace app immediately."
            : "They regain access immediately.") +
          "\n\nThis changes the customer's tenant and is recorded in the audit log.",
      )
    )
      return;
    setBusy(`susp-${u.primary_email}`);
    setErr("");
    try {
      await api.post(
        `/api/v1/cloud-connectors/${current.id}/gws-users/` +
          `${encodeURIComponent(u.primary_email)}/${suspend ? "suspend" : "unsuspend"}`,
      );
      flash(`${u.primary_email} ${suspend ? "suspended" : "restored"}.`);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || `Could not ${verb.toLowerCase()} ${u.primary_email}.`);
    } finally {
      setBusy(null);
    }
  };

  const createUser = async () => {
    if (!current) return;
    setBusy("create");
    setErr("");
    try {
      await api.post(`/api/v1/cloud-connectors/${current.id}/gws-users`, newUser);
      setCreating(false);
      setNewUser({ primary_email: "", given_name: "", family_name: "", password: "" });
      flash("User created. They will be asked to change the password at first sign-in.");
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Could not create that user.");
    } finally {
      setBusy(null);
    }
  };

  const resetPassword = async () => {
    if (!current || !resetting) return;
    setBusy("reset");
    setErr("");
    try {
      await api.post(
        `/api/v1/cloud-connectors/${current.id}/gws-users/` +
          `${encodeURIComponent(resetting.primary_email)}/reset-password`,
        { new_password: newPassword },
      );
      flash(
        `Password reset for ${resetting.primary_email}. It is not stored — give it to them now.`,
      );
      setResetting(null);
      setNewPassword("");
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Could not reset that password.");
    } finally {
      setBusy(null);
    }
  };

  const addMember = async () => {
    if (!current || !addingTo) return;
    setBusy("addmember");
    setErr("");
    try {
      await api.post(
        `/api/v1/cloud-connectors/${current.id}/gws-groups/` +
          `${encodeURIComponent(addingTo.email)}/members`,
        newMember,
      );
      flash(`${newMember.member_email} added to ${addingTo.email}.`);
      const g = addingTo;
      setAddingTo(null);
      setNewMember({ member_email: "", role: "MEMBER" });
      await load();
      setOpenGroup(null);
      await loadMembers(g);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Could not add that member.");
    } finally {
      setBusy(null);
    }
  };

  const removeMember = async (g: WsGroup, m: WsMember) => {
    if (!current) return;
    if (!window.confirm(`Remove ${m.email} from ${g.email}?`)) return;
    setBusy(`rm-${m.email}`);
    setErr("");
    try {
      await api.delete(
        `/api/v1/cloud-connectors/${current.id}/gws-groups/` +
          `${encodeURIComponent(g.email)}/members/${encodeURIComponent(m.email)}`,
      );
      flash(`${m.email} removed from ${g.email}.`);
      setMembers((prev) => prev.filter((x) => x.email !== m.email));
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Could not remove that member.");
    } finally {
      setBusy(null);
    }
  };

  const needle = q.trim().toLowerCase();
  const shownUsers = needle
    ? users.filter((u) =>
        [u.primary_email, u.name, u.ou_path].some((v) => (v || "").toLowerCase().includes(needle)),
      )
    : users;
  const shownGroups = needle
    ? groups.filter((g) =>
        [g.email, g.name, g.description].some((v) => (v || "").toLowerCase().includes(needle)),
      )
    : groups;

  /* Publish what is on screen to the AI Assist.
   *
   * The FILTERED lists, not the raw ones: when someone has typed a search and
   * then asks the assistant about "these users", they mean the rows they can
   * see. Publishing everything would answer a question they did not ask.
   */
  usePublishScreenData(
    "Cloud identity",
    useMemo(
      () => [
        ...shownUsers.map((u: any) => ({
          kind: "user",
          name: u.name || `${u.given_name || ""} ${u.family_name || ""}`.trim() || u.primary_email,
          email: u.primary_email,
          admin: !!u.is_admin,
          suspended: !!u.suspended,
          path: u.ou_path || "/",
        })),
        ...shownGroups.map((g: any) => ({
          kind: "group",
          name: g.name,
          email: g.email,
          description: g.description,
        })),
      ],
      [shownUsers, shownGroups],
    ),
    `${shownUsers.length} user(s) and ${shownGroups.length} group(s)` +
      (needle ? ` matching "${needle}"` : "") +
      (current ? ` on the "${current.name}" connection.` : "."),
  );

  const input =
    "w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100";

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Users &amp; groups</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Google Workspace accounts and groups for a connected customer — onboard, offboard, reset
          a password and change group membership without opening the admin console.
        </p>
      </div>

      {err && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm px-4 py-2 rounded-lg">
          {err}
        </div>
      )}
      {msg && (
        <div className="mb-4 bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-sm px-4 py-2 rounded-lg">
          {msg}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select
          value={selected}
          onChange={(e) => {
            setSelected(e.target.value ? Number(e.target.value) : "");
            setOpenGroup(null);
          }}
          className="border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        >
          <option value="">Choose a connector…</option>
          {usable.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} · {c.platform_label}
            </option>
          ))}
        </select>
        {current && (
          <>
            <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-50 dark:bg-gray-900/50">
              {(
                [
                  ["users", `Users${users.length ? ` (${users.length})` : ""}`],
                  ["groups", `Groups${groups.length ? ` (${groups.length})` : ""}`],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={`px-3 py-1.5 text-xs rounded-md ${
                    tab === key
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter…"
              className="border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 w-56"
            />
            <button
              onClick={load}
              disabled={loading}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
            {tab === "users" && canAdminUsers && (
              <button
                onClick={() => setCreating(true)}
                className="px-3 py-1.5 text-sm rounded-lg bg-brand text-white hover:opacity-90"
              >
                New user
              </button>
            )}
          </>
        )}
      </div>

      {usable.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 text-sm text-gray-500 dark:text-gray-400">
          No Google Workspace connector for this customer yet.{" "}
          <Link to="/cloud" className="text-brand dark:text-brand-light hover:underline">
            Connect one
          </Link>
          .
        </div>
      )}

      {current && tab === "users" && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <table className="w-full text-sm md:min-w-[860px]">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">Email</th>
                <th className="text-left px-3 py-2 font-medium">Org unit</th>
                <th className="text-left px-3 py-2 font-medium">Last sign-in</th>
                <th className="text-left px-3 py-2 font-medium">State</th>
                <th className="text-left px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {shownUsers.map((u) => (
                <tr key={u.id} className="text-gray-900 dark:text-gray-100">
                  <td className="px-3 py-2">
                    {u.name || `${u.given_name || ""} ${u.family_name || ""}`.trim() || "—"}
                    {u.is_admin && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300">
                        admin
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{u.primary_email}</td>
                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                    {u.ou_path || "/"}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                    {u.last_login_at ? formatDateTime(u.last_login_at) : "never"}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {u.is_suspended ? (
                      <span className="text-red-600 dark:text-red-400">suspended</span>
                    ) : (
                      <span className="text-emerald-600 dark:text-emerald-400">active</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {canAdminUsers ? (
                      <>
                        <button
                          onClick={() => setSuspended(u, !u.is_suspended)}
                          disabled={busy === `susp-${u.primary_email}`}
                          className="text-xs text-brand dark:text-brand-light hover:underline mr-3 disabled:opacity-50"
                        >
                          {busy === `susp-${u.primary_email}`
                            ? "Working…"
                            : u.is_suspended
                              ? "Restore"
                              : "Suspend"}
                        </button>
                        <button
                          onClick={() => {
                            setResetting(u);
                            setNewPassword("");
                          }}
                          className="text-xs text-brand dark:text-brand-light hover:underline"
                        >
                          Reset password
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500">read-only</span>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && shownUsers.length === 0 && (
                <tr>
                  <td
                    className="px-3 py-6 text-center text-gray-500 dark:text-gray-400 italic text-xs"
                    colSpan={6}
                  >
                    {users.length ? "Nothing matches that filter." : "No users returned."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {current && tab === "groups" && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <table className="w-full text-sm md:min-w-[760px]">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Group</th>
                <th className="text-left px-3 py-2 font-medium">Address</th>
                <th className="text-left px-3 py-2 font-medium">Members</th>
                <th className="text-left px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {/* A keyed Fragment, not <>: each group renders TWO rows (the
                  row and its expanded member list), and the shorthand
                  fragment cannot take a key - React would lose track of which
                  group is which as the list re-sorts. */}
              {shownGroups.map((g) => (
                <Fragment key={g.id}>
                  <tr className="text-gray-900 dark:text-gray-100">
                    <td className="px-3 py-2">
                      {g.name || g.email}
                      {g.description && (
                        <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[22rem]">
                          {g.description}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">{g.email}</td>
                    <td className="px-3 py-2 text-xs">{g.direct_members_count}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {canReadMembers && (
                        <button
                          onClick={() => loadMembers(g)}
                          className="text-xs text-brand dark:text-brand-light hover:underline mr-3"
                        >
                          {openGroup === g.email ? "Hide members" : "Members"}
                        </button>
                      )}
                      {canAdminGroups && (
                        <button
                          onClick={() => {
                            setAddingTo(g);
                            setNewMember({ member_email: "", role: "MEMBER" });
                          }}
                          className="text-xs text-brand dark:text-brand-light hover:underline"
                        >
                          Add member
                        </button>
                      )}
                    </td>
                  </tr>
                  {openGroup === g.email && (
                    <tr>
                      <td colSpan={4} className="px-3 pb-3 bg-gray-50 dark:bg-gray-900/40">
                        {membersLoading ? (
                          <div className="py-3 text-xs text-gray-500 dark:text-gray-400">
                            Loading members…
                          </div>
                        ) : members.length === 0 ? (
                          <div className="py-3 text-xs text-gray-500 dark:text-gray-400 italic">
                            No members.
                          </div>
                        ) : (
                          <ul className="py-2 space-y-1">
                            {members.map((m) => (
                              <li key={m.id || m.email} className="flex items-center gap-3 text-xs">
                                <span className="text-gray-900 dark:text-gray-100">{m.email}</span>
                                <span className="text-gray-500 dark:text-gray-400">
                                  {[m.role, m.type, m.status].filter(Boolean).join(" · ")}
                                </span>
                                {canAdminGroups && (
                                  <button
                                    onClick={() => removeMember(g, m)}
                                    disabled={busy === `rm-${m.email}`}
                                    className="text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                                  >
                                    {busy === `rm-${m.email}` ? "Removing…" : "Remove"}
                                  </button>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {!loading && shownGroups.length === 0 && (
                <tr>
                  <td
                    className="px-3 py-6 text-center text-gray-500 dark:text-gray-400 italic text-xs"
                    colSpan={4}
                  >
                    {groups.length ? "Nothing matches that filter." : "No groups returned."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── new user ─────────────────────────────────────────────────────── */}
      {creating && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-40 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md my-8">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                New Workspace user
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Created in {current?.name}. They are asked to change the password at first
                sign-in.
              </p>
            </div>
            <div className="px-5 py-4 space-y-3">
              {err && (
                <div className="bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs px-3 py-2 rounded-lg">
                  {err}
                </div>
              )}
              <label className="block text-xs text-gray-500 dark:text-gray-400">
                Email address
                <input
                  value={newUser.primary_email}
                  onChange={(e) => setNewUser({ ...newUser, primary_email: e.target.value })}
                  placeholder="first.last@customer.com"
                  className={`mt-1 ${input}`}
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs text-gray-500 dark:text-gray-400">
                  First name
                  <input
                    value={newUser.given_name}
                    onChange={(e) => setNewUser({ ...newUser, given_name: e.target.value })}
                    className={`mt-1 ${input}`}
                  />
                </label>
                <label className="block text-xs text-gray-500 dark:text-gray-400">
                  Last name
                  <input
                    value={newUser.family_name}
                    onChange={(e) => setNewUser({ ...newUser, family_name: e.target.value })}
                    className={`mt-1 ${input}`}
                  />
                </label>
              </div>
              <label className="block text-xs text-gray-500 dark:text-gray-400">
                Temporary password
                <input
                  type="text"
                  autoComplete="off"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className={`mt-1 ${input} font-mono`}
                />
                <span className="block mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">
                  Shown as you type because you have to pass it on — it is never stored and
                  cannot be read back.
                </span>
              </label>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setCreating(false)}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                Cancel
              </button>
              <button
                onClick={createUser}
                disabled={
                  busy === "create" || !newUser.primary_email.trim() || !newUser.password.trim()
                }
                className="px-4 py-1.5 text-sm rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50"
              >
                {busy === "create" ? "Creating…" : "Create user"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── reset a password ─────────────────────────────────────────────── */}
      {resetting && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-40 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md my-8">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Reset password
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {resetting.primary_email}
              </p>
            </div>
            <div className="px-5 py-4 space-y-3">
              {err && (
                <div className="bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs px-3 py-2 rounded-lg">
                  {err}
                </div>
              )}
              <label className="block text-xs text-gray-500 dark:text-gray-400">
                New password
                <input
                  type="text"
                  autoComplete="off"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={`mt-1 ${input} font-mono`}
                />
                <span className="block mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">
                  Copy it now. AeroGauge does not keep it, and no endpoint will return it.
                </span>
              </label>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setResetting(null)}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                Cancel
              </button>
              <button
                onClick={resetPassword}
                disabled={busy === "reset" || !newPassword.trim()}
                className="px-4 py-1.5 text-sm rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50"
              >
                {busy === "reset" ? "Resetting…" : "Reset password"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── add a group member ───────────────────────────────────────────── */}
      {addingTo && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-40 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md my-8">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add member</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{addingTo.email}</p>
            </div>
            <div className="px-5 py-4 space-y-3">
              {err && (
                <div className="bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs px-3 py-2 rounded-lg">
                  {err}
                </div>
              )}
              <label className="block text-xs text-gray-500 dark:text-gray-400">
                Member email
                <input
                  list="ws-users"
                  value={newMember.member_email}
                  onChange={(e) => setNewMember({ ...newMember, member_email: e.target.value })}
                  className={`mt-1 ${input}`}
                />
                {/* Typing an address that is not in the tenant is the usual way
                    this fails, so offer the ones that are. */}
                <datalist id="ws-users">
                  {users.map((u) => (
                    <option key={u.id} value={u.primary_email} />
                  ))}
                </datalist>
              </label>
              <label className="block text-xs text-gray-500 dark:text-gray-400">
                Role
                <select
                  value={newMember.role}
                  onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                  className={`mt-1 ${input}`}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setAddingTo(null)}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                Cancel
              </button>
              <button
                onClick={addMember}
                disabled={busy === "addmember" || !newMember.member_email.trim()}
                className="px-4 py-1.5 text-sm rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50"
              >
                {busy === "addmember" ? "Adding…" : "Add member"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
